import type { SupportedOAuthFlow } from './oauth-types';
import type Oas from 'oas';
import type { Operation } from 'oas/operation';

/**
 * Determines the proxyEnabled value for an OAuth scheme by checking in this order:
 * 1. OAuth flow definition level (e.g., flows.clientCredentials.x-readme.proxy-enabled)
 * 2. Operation level (via getExtension)
 * 3. Root level (via getExtension fallback)
 *
 * NOTE: This proxyEnabled value is ONLY used for OAuth token/authorize requests.
 * Regular API endpoint requests use apiDefinition.getExtension('proxy-enabled', operation) directly,
 * which does NOT check the OAuth flow definition level.
 */
export function getProxyEnabledForOAuthScheme(
  apiDefinition: Oas,
  operation: Operation,
  schemeKey: string,
  firstSupportedFlow: SupportedOAuthFlow | undefined,
): boolean | undefined {
  let proxyEnabled: boolean | undefined;

  const rawSecurityScheme = apiDefinition.api?.components?.securitySchemes?.[schemeKey] as
    | {
        flows?: Record<string, { 'x-readme'?: { 'proxy-enabled'?: boolean } }>;
      }
    | undefined;

  // Check flow definition level first
  if (rawSecurityScheme?.flows && firstSupportedFlow) {
    const flowDefinition = rawSecurityScheme.flows[firstSupportedFlow];
    proxyEnabled = flowDefinition?.['x-readme']?.['proxy-enabled'];
  }

  // Only use getExtension if flow level didn't find a value
  // getExtension checks operation level, then root level
  if (proxyEnabled === undefined) {
    const operationProxy = apiDefinition.getExtension('proxy-enabled', operation) as boolean | undefined;
    const hasOperationExtension =
      operation?.schema?.['x-readme']?.['proxy-enabled'] !== undefined ||
      operation?.schema?.['x-proxy-enabled'] !== undefined;
    const hasRootExtension =
      apiDefinition.api?.['x-readme']?.['proxy-enabled'] !== undefined ||
      apiDefinition.api?.['x-proxy-enabled'] !== undefined;

    // Only use getExtension's result if the extension actually exists
    if (hasOperationExtension || hasRootExtension) {
      proxyEnabled = operationProxy;
    } else {
      // Default to true if extension doesn't exist anywhere
      proxyEnabled = true;
    }
  }

  return proxyEnabled;
}
